import { useState } from 'react'
import { formatScorecardDecimal } from 'helpers/scorecardFormat'
import {
  List,
  CreateButton,
  Table,
  useTable,
  useModalForm,
  RefreshButton,
  DateField,
  DeleteButton,
  Icons,
  Button,
  message,
  Space,
  Input,
  Form,
  Divider,
  Row,
  Col,
  ExportButton,
} from '@pankod/refine-antd'
import { useExport } from '@pankod/refine-core'
import { CreateNationalAverage } from './create'
import { deleteItemById } from 'gql/mutations'
import { INationalAverage } from 'type'
export const NationalAverageList = () => {
  const { tableProps, tableQueryResult, searchFormProps } = useTable({
    resource: 'national_average',
    initialSorter: [
      {
        field: 'test_track_name',
        order: 'desc',
      },
    ],
    metaData: {
      fields: ['id', 'product_name', 'test_track_name', 'section_wise_average', 'total_national_average', 'created_at', 'tq', 'type'],
    },
    // queryOptions: { staleTime: Infinity },
    onSearch(data: any) {
      return [{ field: 'test_track_name', operator: 'contains', value: `%${data?.name}%` }]
    },
  })

  const [isLoading, setIsLoading] = useState(false)

  const {
    formProps,
    modalProps: createModalProps,
    show: showCreateModal,
    close: closeCreateModal,
  } = useModalForm({
    resource: 'national_average',
    action: 'create',
    redirect: false,
  })
  const createButtonProps = {
    onClick: () => {
      setIsLoading(true)
      showCreateModal()
    },
    loading: isLoading,
  }

  const refetchData = () => {
    tableQueryResult?.refetch()
  }
  const handleDelete = async (id: string) => {
    const result: any = await deleteItemById(id, 'national_average')
    if (result?.delete_national_average_by_pk?.id) {
      await refetchData()
      message.success('item deleted successfully!')
    } else {
      message.error("can't delete now!")
    }
  }

  const isDateIsValid = (value: any) => {
    const originalDate = new Date(value)
    const modifiedDate = new Date(originalDate.getTime() + 30 * 24 * 60 * 60 * 1000)
    const isDateValid = modifiedDate > new Date()
    return { isDateValid, modifiedDate }
  }
  const { triggerExport: exportNationalAverageDetails, isLoading: nationalAverageExportLoading } = useExport<INationalAverage>({
    resourceName: 'national_average',
    metaData: {
      fields: ['id', 'section_wise_average', 'product_id', 'test_track_id', 'product_name', 'test_track_name', 'total_national_average', 'type'],
    },
    exportOptions: {
      filename: `national_average_${Date.now()}`,
    },
    maxItemCount: 100000,
    mapData: record => {
      if (record) {
        
        return {
          "Test Track": record?.test_track_name || 'N/A',
          "Product": record?.product_name || 'N/A',
          "Type": record?.type || 'N/A',
          "Total National Average": record?.total_national_average || 'N/A',
          "Sectional National Average": JSON.stringify(record?.section_wise_average)|| 'N/A',
        }
      }
    },
    onError: () => {
      message.error('Export failed, please try again later...')
    },
  })
  return (
    <List title="National Average">
      <Row gutter={[24, 16]}>
        <Col xs={24} sm={24} md={14} lg={16} xl={18}>
          <Form {...searchFormProps}>
            <Space>
              <Form.Item name="name">
                <Input
                  placeholder="Search by test track..."
                  suffix={
                    <>
                      <Icons.SearchOutlined style={{ cursor: 'pointer' }} onClick={searchFormProps.form?.submit} />
                      <Icons.CloseCircleOutlined
                        style={{ cursor: 'pointer', marginLeft: '8px' }}
                        onClick={() => {
                          searchFormProps?.form?.setFieldsValue({ name: '' })
                          searchFormProps.form?.submit()
                        }}
                      />
                    </>
                  }
                />
              </Form.Item>
            </Space>
          </Form>
        </Col>
        <Col xs={24} sm={24} md={10} lg={8} xl={6}>
          <Space>
            <CreateButton {...createButtonProps} />
            <RefreshButton onClick={refetchData} />
            <ExportButton onClick={exportNationalAverageDetails} loading={nationalAverageExportLoading} />
          </Space>
        </Col>
      </Row>

      <Divider />

      <Table {...tableProps} rowKey="id" size="small" loading={tableQueryResult?.isFetching}>
        <Table.Column dataIndex="test_track_name" title="Test Track Name" />
        <Table.Column dataIndex="product_name" title="Product Name" render={val => val || 'N/A'} />
        <Table.Column dataIndex="type" title="Type" render={value => value.toUpperCase() || ''} />
        <Table.Column dataIndex="total_national_average" title="Total National Average" render={value => formatScorecardDecimal(value)} />
        <Table.Column
          dataIndex="section_wise_average"
          title="Sectional National Average"
          render={value => {
            if (value && value?.length > 0) {
              return value.map((item: any, index: number) => (
                <div key={index}>
                  {item.name}:<span style={{ fontWeight: 'bold' }}>&nbsp;&nbsp;{formatScorecardDecimal(item.sectional_national_average)}</span>
                </div>
              ))
            }
            return '-'
          }}
        />
        <Table.Column dataIndex="created_at" title="Created / Updated At" render={value => <DateField value={value} format="LLL" />} />
        <Table.Column
          dataIndex="created_at"
          title="Validity Up To"
          render={value => {
            const isValid = isDateIsValid(value)
            const colorStyle = {
              color: isValid?.isDateValid ? 'green' : 'red',
            }
            return <DateField value={isValid?.modifiedDate} format="LLL" style={colorStyle} />
          }}
        />
        <Table.Column
          dataIndex="id"
          title="Actions"
          render={(value, record: any) => {
            const isValid = isDateIsValid(record?.created_at)
            return (
              <Button
                size="small"
                onClick={() => handleDelete(value)}
                style={{ color: 'red', visibility: isValid?.isDateValid ? 'hidden' : 'visible' }}
              >
                Delete
              </Button>
            )
          }}
        />
      </Table>

      <CreateNationalAverage
        formProps={formProps}
        modalProps={createModalProps}
        refetchData={refetchData}
        close={() => {
          setIsLoading(false)
          closeCreateModal()
        }}
      />
    </List>
  )
}
